import React, { useState, useCallback, useRef, useEffect } from 'react';

export function useCelebration(durationMs = 2200) {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  const celebrate = useCallback(() => {
    setShow(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShow(false);
      timerRef.current = null;
    }, durationMs);
  }, [durationMs]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return [show, celebrate];
}

export function CelebrationOverlay({ visible, message = 'You did it!' }) {
  if (!visible) return null;

  return (
    <>
      <style>
        {`
          @keyframes engine-celebrate-pop {
            0% { transform: scale(0.65); opacity: 0; }
            55% { transform: scale(1.06); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes engine-sparkle {
            0%, 100% { opacity: 0.4; transform: translateY(0) scale(1); }
            50% { opacity: 1; transform: translateY(-8px) scale(1.1); }
          }
        `}
      </style>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          background: 'rgba(0,0,0,0.12)',
        }}
      >
        <div
          style={{
            animation: 'engine-celebrate-pop 0.55s ease-out forwards',
            background: 'var(--color-bg-card)',
            border: '3px solid var(--color-primary)',
            borderRadius: 24,
            padding: '28px 36px',
            boxShadow: '0 12px 40px var(--color-shadow)',
            textAlign: 'center',
            maxWidth: 'min(90vw, 360px)',
          }}
        >
          <div
            style={{
              fontSize: 52,
              lineHeight: 1,
              animation: 'engine-sparkle 0.9s ease-in-out infinite',
            }}
            aria-hidden
          >
            ✨
          </div>
          <div
            style={{
              marginTop: 12,
              fontSize: 22,
              fontWeight: 800,
              color: 'var(--color-text)',
            }}
          >
            {message}
          </div>
        </div>
      </div>
    </>
  );
}

/** Screen reader updates for feedback (pair with visible feedback). */
export function A11yAnnouncement({ message }) {
  if (!message) return null;
  return (
    <span className="visually-hidden" style={srOnly}>
      {message}
    </span>
  );
}

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};
